import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Heart, Car, Plane, Search, CheckCircle, ArrowRight, Users, Star, TrendingUp, Award, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useSettings } from '@/contexts/SettingsContext';
import api from '@/api/axios';
import BannerSlider from '@/components/BannerSlider';
import OAuthCallback from '@/components/OAuthCallback';

const HomePage = () => {
  const { settings } = useSettings();
  const [featuredPolicies, setFeaturedPolicies] = useState([]);
  const [bannerSlides, setBannerSlides] = useState([]);

  useEffect(() => {
    fetchFeaturedPolicies();
    fetchBannerSlides();
  }, []);

  const fetchFeaturedPolicies = async () => {
    try {
      const response = await api.get('/policies', { params: { popular: true } });
      setFeaturedPolicies(response.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch featured policies:', error);
    }
  };

  const fetchBannerSlides = async () => {
    try {
      const response = await api.get('/banner-slides');
      setBannerSlides(response.data);
    } catch (error) {
      console.error('Failed to fetch banner slides:', error);
    }
  };

  const insuranceTypes = [
    {
      icon: Shield,
      name: 'Health Insurance',
      type: 'Health',
      description: 'Comprehensive medical coverage',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      badge: null
    },
    {
      icon: Heart,
      name: 'Life Insurance',
      type: 'Life',
      description: 'Secure your family\'s future',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      badge: 'Save Tax*'
    },
    {
      icon: Car,
      name: 'Motor Insurance',
      type: 'Motor',
      description: 'Complete vehicle protection',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      badge: 'Up to 25% Off*'
    },
    {
      icon: Plane,
      name: 'Travel Insurance',
      type: 'Travel',
      description: 'Safe and protected journeys',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      badge: null
    }
  ];

  const stats = [
    { icon: Users, value: '10,000+', label: 'Happy Customers', color: 'text-blue-600' },
    { icon: Star, value: '4.8', label: 'Rated on Google', color: 'text-yellow-500' },
    { icon: TrendingUp, value: '500+', label: 'Claims Settled', color: 'text-green-600' },
    { icon: Award, value: '100%', label: 'Claim Support', color: 'text-purple-600' }
  ];

  const benefits = [
    {
      icon: CheckCircle,
      title: '5 Minutes Policy Comparison',
      description: 'Quick and easy comparison of multiple policies. Compare features, coverage and premiums instantly.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      icon: Users,
      title: 'Expert Guidance',
      description: 'Get assistance from our dedicated support team. We help you choose the right policy for your needs.',
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      icon: Shield,
      title: 'Trusted Partners',
      description: 'Compare policies from India\'s top insurance companies. All policies verified and authentic.',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    }
  ];

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      policy: 'Health Insurance',
      rating: 5,
      comment: 'Simple and fast process! I compared 4 health policies and found the perfect one for my family. The side-by-side comparison made it so easy.',
      date: 'February 15, 2025'
    },
    {
      name: 'Priya Sharma',
      policy: 'Motor Insurance',
      rating: 5,
      comment: 'Excellent platform for comparing motor insurance. Clear information about coverage and premiums helped me make the right choice.',
      date: 'February 10, 2025'
    },
    {
      name: 'Amit Patel',
      policy: 'Life Insurance',
      rating: 5,
      comment: 'Very transparent and user-friendly. Loved how I could compare all the features in one table. Highly recommended!',
      date: 'February 5, 2025'
    }
  ];

  const howItWorks = [
    { step: '1', title: 'Choose Insurance Type', description: 'Select from Health, Life, Motor, or Travel' },
    { step: '2', title: 'Compare Policies', description: 'Review features, premiums, and coverage side-by-side' },
    { step: '3', title: 'Make Informed Decision', description: 'Select the policy that best fits your needs' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      <OAuthCallback />
      {/* Banner Slider */}
      {bannerSlides.length > 0 && <BannerSlider slides={bannerSlides} />}

      {/* Trust Stats */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {settings.stats && settings.stats.map((stat, index) => {
              const iconMap = [Users, Star, TrendingUp, Award];
              const colorMap = ['text-blue-600', 'text-yellow-500', 'text-green-600', 'text-purple-600'];
              const Icon = iconMap[index] || Users;
              const color = colorMap[index] || 'text-blue-600';
              
              return (
                <div key={index} className="text-center" data-testid={`stat-${index}`}>
                  <div className="flex justify-center mb-3">
                    <div className={`w-14 h-14 rounded-full ${color.replace('text-', 'bg-').replace('600', '100')} flex items-center justify-center`}>
                      <Icon className={`h-7 w-7 ${color}`} />
                    </div>
                  </div>
                  <p className="text-3xl font-bold font-mono text-secondary-900 mb-1">{stat.value}</p>
                  <p className="text-sm text-secondary-600">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Insurance Types */}
      <section className="py-16 md:py-24 bg-secondary-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold font-heading text-secondary-900 mb-4">
              Insurance Types
            </h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              Choose from a wide range of insurance options tailored to your needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {insuranceTypes.map((type, index) => {
              const Icon = type.icon;
              return (
                <Link 
                  to={`/policies?type=${type.type}`} 
                  key={index}
                  data-testid={`insurance-type-${type.type.toLowerCase()}`}
                >
                  <Card className="h-full hover:shadow-lg transition-all duration-200 hover:-translate-y-1 cursor-pointer border-2 border-secondary-200 hover:border-primary-400 relative overflow-hidden">
                    {type.badge && (
                      <div className="absolute top-3 right-3">
                        <Badge className="bg-green-500 text-white text-xs px-2 py-1 hover:bg-green-500">
                          {type.badge}
                        </Badge>
                      </div>
                    )}
                    <CardContent className="p-6">
                      <div className={`w-16 h-16 rounded-xl ${type.bgColor} flex items-center justify-center mb-4`}>
                        <Icon className={`h-8 w-8 ${type.color}`} />
                      </div>
                      <h3 className="text-xl font-semibold text-secondary-900 mb-2">{type.name}</h3>
                      <p className="text-sm text-secondary-600">{type.description}</p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-secondary-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold font-heading text-secondary-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              Compare policies in three simple steps
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((item, index) => (
              <div key={index} className="text-center" data-testid={`how-it-works-step-${item.step}`}>
                <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-2xl font-bold font-heading mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-2">{item.title}</h3>
                <p className="text-base text-secondary-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold font-heading text-secondary-900 mb-4">
              Benefits of Noble Shield
            </h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              Understand your insurance policy options. Identify the best value. Enjoy peace of mind.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="border-2 hover:shadow-lg transition-all duration-200" data-testid={`feature-card-${index}`}>
                  <CardHeader>
                    <div className={`w-16 h-16 ${benefit.bgColor} rounded-xl flex items-center justify-center mb-4`}>
                      <Icon className={`h-8 w-8 ${benefit.color}`} />
                    </div>
                    <CardTitle className="text-xl font-semibold text-secondary-900">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-base text-secondary-600 leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Policies */}
      {featuredPolicies.length > 0 && (
        <section className="py-16 md:py-24 bg-secondary-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold font-heading text-secondary-900 mb-4">
                Popular Insurance Plans
              </h2>
              <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
                Most chosen policies by our customers
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredPolicies.map((policy) => (
                <Card key={policy.id} className="hover:shadow-xl transition-all duration-200 border-2 border-secondary-200 hover:border-primary-400" data-testid={`featured-policy-${policy.id}`}>
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="secondary" className="text-xs">
                        {policy.category_type}
                      </Badge>
                      <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-xs">
                        Popular
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-semibold text-secondary-900">
                      {policy.name}
                    </CardTitle>
                    <CardDescription className="text-sm text-secondary-500">
                      {policy.provider_name}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <p className="text-sm text-secondary-600 mb-1">Cover Amount</p>
                      <p className="text-2xl font-bold font-mono text-primary-700">
                        {formatCurrency(policy.coverage_amount)}
                      </p>
                    </div>
                    <div className="mb-4">
                      <p className="text-sm text-secondary-600 mb-1">Starting at</p>
                      <div className="flex items-baseline gap-1">
                        <p className="text-3xl font-bold font-mono text-secondary-900">
                          {formatCurrency(policy.premium_yearly / 12)}
                        </p>
                        <span className="text-sm text-secondary-600">/month</span>
                      </div>
                    </div>
                    <ul className="space-y-2 text-sm text-secondary-700">
                      {policy.key_benefits?.slice(0, 2).map((benefit, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-1">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardFooter className="flex-col gap-2">
                    <Link to={`/policy/${policy.id}`} className="w-full">
                      <Button variant="outline" className="w-full" data-testid={`view-policy-${policy.id}`}>
                        View Details
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold font-heading text-secondary-900 mb-4">
              Customer's Speak
            </h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              Know why they chose Noble Shield
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="hover:shadow-lg transition-all duration-200" data-testid={`testimonial-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-base text-secondary-700 leading-relaxed mb-4">
                    "{testimonial.comment}"
                  </p>
                  <div className="pt-4 border-t border-secondary-200">
                    <p className="font-semibold text-secondary-900">{testimonial.name}</p>
                    <p className="text-sm text-secondary-600">{testimonial.policy}</p>
                    <p className="text-xs text-secondary-500 mt-1">{testimonial.date}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading text-white mb-4">
            Ready to Compare?
          </h2>
          <p className="text-lg text-primary-100 mb-8">
            Start comparing insurance policies today and find the perfect coverage for your needs
          </p>
          <Link to="/policies">
            <Button size="lg" variant="secondary" className="bg-white text-primary-600 hover:bg-primary-50" data-testid="cta-get-started-button">
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
